import { useSpeckleAuthStore, useSpeckleStore } from "@haskoning/vue-speckle";
import { computed, watch } from "vue";

type SpeckleSelectionContext = {
  projectId: string;
  projectName?: string;
  modelId: string;
  modelName?: string;
  versionId: string;
  receiveUrl: string;
  speckleToken: string;
};

export function useSpeckleContextSelection() {
  const speckleAuthStore = useSpeckleAuthStore();
  const speckleStore = useSpeckleStore({ autoSelectData: false });

  const isSpeckleAuthenticated = computed<boolean>(() => speckleAuthStore.isAuthenticated);
  const hasSelectedProject = computed<boolean>(() => Boolean(speckleStore.currentSelection.project?.id));
  const hasSelectedModel = computed<boolean>(() => Boolean(speckleStore.currentSelection.model?.id));
  const hasSelectedVersion = computed<boolean>(() => Boolean(speckleStore.currentSelection.version?.id));

  const isContextComplete = computed<boolean>(
    () =>
      isSpeckleAuthenticated.value &&
      hasSelectedProject.value &&
      hasSelectedModel.value &&
      hasSelectedVersion.value &&
      Boolean(speckleStore.receiveUrl())
  );

  watch(
    () => speckleStore.currentSelection.project?.id,
    () => {
      speckleStore.currentSelection.model = undefined;
      speckleStore.currentSelection.version = undefined;
    }
  );

  /** The full receive context, or null when anything required is still missing. */
  function getSelectionContext(): SpeckleSelectionContext | null {
    const project = speckleStore.currentSelection.project;
    const model = speckleStore.currentSelection.model;
    const version = speckleStore.currentSelection.version;
    const receiveUrl = speckleStore.receiveUrl();
    const speckleToken = speckleAuthStore.token;

    if (!project?.id || !model?.id || !version?.id || !receiveUrl || !speckleToken) {
      return null;
    }

    return {
      projectId: project.id,
      projectName: project.name,
      modelId: model.id,
      modelName: model.name,
      versionId: version.id,
      receiveUrl,
      speckleToken,
    };
  }

  return {
    hasSelectedProject,
    hasSelectedModel,
    isContextComplete,
    getSelectionContext,
  };
}
